import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import classnames from 'classnames/bind';
import { formatProportion } from './shared';
import styles from './ProportionHorizontalBarChart.module.css';

const cx = classnames.bind(styles);

export default class ProportionHorizontalBarChart extends React.Component {
  static propTypes = {
    rowLabels: PropTypes.arrayOf(PropTypes.string),
    rowValues: PropTypes.arrayOf(PropTypes.number),
    leftColor: PropTypes.string,
    rightColor: PropTypes.string,
  };

  ref = React.createRef();

  get svg() {
    return d3.select(this.ref.current);
  }

  get rect() {
    return this.ref.current.getBoundingClientRect();
  }

  componentDidMount() {
    this.draw();
  }

  componentDidUpdate() {
    this.draw();
  }

  draw() {
    const { rowLabels, rowValues, leftColor, rightColor } = this.props;

    const svg = this.svg;
    const { width, height } = this.rect;
    const margin = { top: 10, right: 30, bottom: 15, left: 30 };

    const yScale = d3
      .scaleBand()
      .domain(rowLabels)
      .rangeRound([margin.top, height - margin.bottom])
      .padding(0.375);

    const xScale = d3
      .scaleLinear()
      .domain([0, 1])
      .rangeRound([margin.left, width - margin.right]);

    // axes
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(2, 's')
      .tickSize(-height + (margin.top + margin.bottom))
      .tickFormat((d) => formatProportion(d));

    // tick marks and vertical lines
    svg
      .append('g')
      .attr('transform', 'translate(0, ' + (height - margin.bottom) + ')')
      .call((g) => {
        g.call(xAxis);
        g.select('.domain').remove();
        g.selectAll('.tick line').attr('stroke', '#DDDDDD');
      });

    // row labels (factor names)
    const pushLabelUpPx = 5;
    const pushLabelRightPx = 2;
    svg
      .selectAll(null)
      .data(rowLabels)
      .enter()
      .append('text')
      .attr('class', cx('label'))
      .attr('x', margin.left + pushLabelRightPx)
      .attr('y', (d, i) => yScale(rowLabels[i]) - pushLabelUpPx)
      .text((d, i) => rowLabels[i]);

    const rectangleSVG = () => {
      return svg
        .selectAll(null)
        .data(rowValues)
        .enter()
        .append('rect')
        .attr('height', yScale.bandwidth())
        .attr('y', (d, i) => yScale(rowLabels[i]));
    };

    // filled proportion (left) rectangles
    rectangleSVG()
      .attr('fill', leftColor)
      .attr('x', xScale(0))
      .attr('width', (d) => xScale(d) - xScale(0));

    // filled remainder (right) rectangles
    rectangleSVG()
      .attr('fill', rightColor)
      .attr('x', (d) => xScale(d))
      .attr('width', (d) => xScale(1.0) - xScale(d));

    const barTextSVG = () => {
      return svg
        .selectAll(null)
        .data(rowValues)
        .enter()
        .append('text')
        .attr('font-size', '12px')
        .style('text-anchor', 'middle')
        .style('alignment-baseline', 'middle')
        .attr('y', (d, i) => yScale(rowLabels[i]) + yScale.bandwidth() / 2);
    };

    // percentage label inside proportion rect
    barTextSVG()
      .attr('x', (d) => (xScale(0) + xScale(d)) * 0.5)
      .text((d) => formatProportion(d));

    // percentage label inside remainder rect
    barTextSVG()
      .attr('x', (d) => (xScale(d) + xScale(1.0)) * 0.5)
      .text((d) => formatProportion(1.0 - d));
  }

  render() {
    return (
      <div className={cx('root')}>
        <div className={cx('box')}>
          <svg width="380" height="450" ref={this.ref} />
        </div>
      </div>
    );
  }
}
